/**
 * Video slider with lazy-loaded media popup.
 * Loads popup script when section scrolls into view or on first slide click.
 *
 * @typedef {{ openWith: (data: object) => void }} VideoPopup
 */
if (!customElements.get("video-slider")) {
	class VideoSlider extends HTMLElement {
		/** @type {HTMLElement[]} */
		slides = [];

		/** @type {HTMLElement[]} */
		dots = [];

		/** @type {number} */
		currentIndex = 0;

		/** @type {HTMLElement | null} */
		prevButton = null;

		/** @type {HTMLElement | null} */
		nextButton = null;

		/** @type {HTMLElement | null} */
		container = null;

		/** @type {IntersectionObserver | null} */
		#intersectionObserver = null;

		/** @type {Promise<void> | null} */
		#popupLoadPromise = null;

		connectedCallback() {
			this.container = this.querySelector("[data-slider-container]");
			this.prevButton = this.querySelector("[data-slider-prev]");
			this.nextButton = this.querySelector("[data-slider-next]");
			this.slides = Array.from(this.querySelectorAll("[data-slider-slide]"));
			this.dots = Array.from(this.querySelectorAll("[data-slider-dot]"));

			if (this.slides.length === 0) return;

			this.#init();
			this.#attachEvents();
			this.#updateSlides();
			this.#observeForLazyLoad();
			window.addEventListener("resize", this.#handleResize);
		}

		disconnectedCallback() {
			this.#removeEvents();
			this.#intersectionObserver?.disconnect();
			window.removeEventListener("resize", this.#handleResize);
		}

		#handleResize = () => {
			this.#updateSlides();
		};

		#init() {
			this.currentIndex = 0;
			if (this.slides.length > 0) this.#updateSlides();
		}

		#attachEvents() {
			if (this.prevButton) this.prevButton.addEventListener("click", this.#handlePrev);
			if (this.nextButton) this.nextButton.addEventListener("click", this.#handleNext);

			for (let i = 0; i < this.dots.length; i++) {
				const dot = this.dots[i];
				if (dot) dot.addEventListener("click", () => this.#goToSlide(i));
			}

			this.slides.forEach((slide) => {
				slide.addEventListener("click", this.#handleSlideClick);
			});
		}

		#removeEvents() {
			if (this.prevButton) this.prevButton.removeEventListener("click", this.#handlePrev);
			if (this.nextButton) this.nextButton.removeEventListener("click", this.#handleNext);
			for (const slide of this.slides) {
				slide.removeEventListener("click", this.#handleSlideClick);
			}
		}

		#handlePrev = () => {
			this.currentIndex =
				this.currentIndex > 0 ? this.currentIndex - 1 : this.slides.length - 1;
			this.#updateSlides();
		};

		#handleNext = () => {
			this.currentIndex =
				this.currentIndex < this.slides.length - 1 ? this.currentIndex + 1 : 0;
			this.#updateSlides();
		};

		/** @param {number} index */
		#goToSlide(index) {
			if (index >= 0 && index < this.slides.length) {
				this.currentIndex = index;
				this.#updateSlides();
			}
		}

		/** @param {MouseEvent} e */
		#handleSlideClick = (e) => {
			const slide = e.currentTarget;
			if (!slide || !(slide instanceof HTMLElement)) return;

			const mediaType = slide.dataset.mediaType;
			if (!mediaType || mediaType === "none") return;

			e.stopPropagation();
			this.#openPopupWithSlide(slide);
		};

		/**
		 * @param {HTMLElement} slide
		 */
		async #openPopupWithSlide(slide) {
			await this.#ensurePopupLoaded();

			const popupId = this.dataset.popupId;
			const popup = /** @type {VideoPopup | null} */ (
				popupId ? document.getElementById(popupId) : null
			);
			if (!popup?.openWith) return;

			const data = this.#getMediaDataFromSlide(slide);
			if (!data) return;

			popup.openWith(data);
		}

		/**
		 * @param {HTMLElement} slide
		 * @returns {{ mediaType: string, imageSrc?: string, imageAlt?: string, imageLink?: string, linkText?: string, videoSources?: Array<{url: string, mime: string}>, videoPoster?: string, linkUrl?: string } | null}
		 */
		#getMediaDataFromSlide(slide) {
			const mediaType = slide.dataset.mediaType;
			if (!mediaType || mediaType === "none") return null;

			if (mediaType === "image") {
				const imageSrc = slide.dataset.imageSrc;
				if (!imageSrc) return null;
				return {
					mediaType: "image",
					imageSrc,
					imageAlt: slide.dataset.imageAlt ?? "",
					imageLink: slide.dataset.imageLink ?? "",
					linkText: slide.dataset.imageLinkText ?? "View",
				};
			}

			if (mediaType === "video_url") {
				const videoId = slide.dataset.videoUrlId;
				const videoType = slide.dataset.videoUrlType;
				if (!videoId || !videoType) return null;
				return {
					mediaType: "video_url",
					videoUrlId: videoId,
					videoUrlType: videoType,
				};
			}

			if (mediaType === "video") {
				const sourcesStr = slide.dataset.videoSources;
				if (!sourcesStr) return null;
				const videoSources = sourcesStr.split(";;").map((part) => {
					const [url, mime] = part.split("|");
					return { url: url ?? "", mime: mime ?? "" };
				});
				return {
					mediaType: "video",
					videoSources,
					videoPoster: slide.dataset.videoPoster ?? "",
				};
			}

			if (mediaType === "link") {
				const linkUrl = slide.dataset.linkUrl;
				if (!linkUrl) return null;
				return {
					mediaType: "link",
					linkUrl,
					linkText: slide.dataset.linkText ?? "Visit",
				};
			}

			return null;
		}

		#ensurePopupLoaded() {
			if (this.#popupLoadPromise) return this.#popupLoadPromise;

			const scriptUrl = this.dataset.popupScript;
			if (!scriptUrl) return Promise.resolve();

			this.#popupLoadPromise = new Promise((resolve, reject) => {
				if (customElements.get("video-media-popup")) {
					resolve();
					return;
				}

				const script = document.createElement("script");
				script.type = "module";
				script.src = scriptUrl;
				script.onload = () => resolve();
				script.onerror = () => reject(new Error("Failed to load video popup"));
				document.head.appendChild(script);
			});

			return this.#popupLoadPromise;
		}

		#observeForLazyLoad() {
			const scriptUrl = this.dataset.popupScript;
			if (!scriptUrl) return;

			this.#intersectionObserver = new IntersectionObserver(
				(entries) => {
					for (const entry of entries) {
						if (entry.isIntersecting) {
							this.#ensurePopupLoaded();
							this.#intersectionObserver?.disconnect();
							break;
						}
					}
				},
				{ rootMargin: "100px" },
			);

			this.#intersectionObserver.observe(this);
		}

		#updateSlides() {
			const total = this.slides.length;
			if (total === 0) return;

			this.slides.forEach((slide, index) => {
				const diff = index - this.currentIndex;
				let position = diff;

				if (total === 1) position = 0;
				else if (total === 2) {
					if (diff === -1 || diff === total - 1) position = -1;
					else if (diff === 1 || diff === -(total - 1)) position = 1;
				} else {
					if (diff < -total / 2) position = diff + total;
					else if (diff > total / 2) position = diff - total;
				}

				this.#applyTransform(slide, position);
			});

			this.#updateDots();
		}

		/**
		 * @param {HTMLElement} slide
		 * @param {number} position
		 */
		#applyTransform(slide, position) {
			if (!slide || typeof position !== "number") return;

			const isMobile = window.matchMedia("(max-width: 639px)").matches;
			const offset = isMobile ? 60 : 120;
			const scale = isMobile ? 0.9 : 0.85;

			const isActive = position === 0;
			const isPrev = position === -1;
			const isNext = position === 1;

			if (isActive) {
				slide.style.opacity = "1";
				slide.style.transform = "none";
				slide.style.zIndex = "10";
			} else if (isPrev) {
				slide.style.opacity = "0.5";
				slide.style.transform = `translateX(-${offset}px) translateZ(-100px) scale(${scale}) rotateY(-15deg)`;
				slide.style.zIndex = "4";
			} else if (isNext) {
				slide.style.opacity = "0.5";
				slide.style.transform = `translateX(${offset}px) translateZ(-100px) scale(${scale}) rotateY(15deg)`;
				slide.style.zIndex = "4";
			} else {
				slide.style.opacity = "0";
				slide.style.transform = "translateX(0) translateZ(-200px) scale(0.7)";
				slide.style.zIndex = "1";
			}
		}

		#updateDots() {
			this.dots.forEach((dot, index) => {
				const isActive = index === this.currentIndex;
				if (isActive) {
					dot.classList.remove("bg-gray-300", "hover:bg-gray-400", "w-3");
					dot.classList.add("bg-[#dc3545]", "w-8");
				} else {
					dot.classList.remove("bg-[#dc3545]", "w-8");
					dot.classList.add("bg-gray-300", "hover:bg-gray-400", "w-3");
				}
			});
		}
	}

	customElements.define("video-slider", VideoSlider);
}
